import { useEffect, useState } from "react";
import { LogOut, X } from "lucide-react";
import type { OrganizationType } from "@bookdental/schemas";
import { DEFAULT_SPECIALTY_CODE } from "@bookdental/schemas";
import { useAuth } from "@/lib/auth";
import { trpc } from "@/lib/trpc";
import { clearPendingConnectInvite, getPendingConnectInvite } from "@/lib/pending-connect-invite";
import { OrgTypeStep } from "./OrgTypeStep";
import { ProfileStep } from "./ProfileStep";
import { EMPTY_ADDRESS, enderecoParaSalvar, type AddressFormValues } from "@/components/profile/AddressFormFields";

type Props = {
  onComplete: () => void;
  /** Quando fornecido, o passo 1 mostra "Cancelar" (chama isso) em vez de "Sair" (logout) — usado
   *  quando o wizard é aberto por escolha (ex: "Criar minha conta pessoal" em Meu perfil), não
   *  como gate obrigatório de primeiro acesso. */
  onCancel?: () => void;
  initialName?: string;
  initialPhone?: string;
};

/**
 * Gate único de perfil — vale pra qualquer conta, inclusive convidados (que já
 * chegam com uma Membership de equipe, mas ainda sem perfil preenchido). Passo 1
 * define a área de atuação da organização pessoal (Clínica/Laboratório); passo 2
 * é o perfil. Ao completar, o backend (me.completeProfile) cria a organização
 * pessoal já com essa área — podendo ser trocada depois em Configurações. Laboratório ganha um
 * passo 3, depois de a organização existir: escolher os serviços da base (que nasce desligada).
 *
 * Reaproveitado também fora do gate: quem entrou via convite de equipe nunca passa por aqui
 * automaticamente (onboardingCompletedAt já vem setado) e por isso nunca ganha organização
 * pessoal — `onCancel` é o sinal de que estamos nesse uso voluntário, não no gate obrigatório.
 */
export function OnboardingWizard({ onComplete, onCancel, initialName = "", initialPhone = "" }: Props) {
  const { user, signOut } = useAuth();
  const utils = trpc.useUtils();
  const [step, setStep] = useState<1 | 2>(1);
  const [types, setTypes] = useState<OrganizationType[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [userName, setUserName] = useState(initialName);
  const [userPhone, setUserPhone] = useState(initialPhone);
  const [cpf, setCpf] = useState("");
  const [croNumber, setCroNumber] = useState("");
  const [croState, setCroState] = useState("");
  const [specialtyIds, setSpecialtyIds] = useState<string[]>([]);
  const [address, setAddress] = useState<AddressFormValues>(EMPTY_ADDRESS);

  const secondaryAction = onCancel
    ? { label: "Cancelar", icon: X, onClick: onCancel }
    : { label: "Sair", icon: LogOut, onClick: signOut };

  const specialtiesQuery = trpc.specialty.list.useQuery();

  // Pré-seleciona "Clínico Geral" assim que o catálogo carrega — nunca fica vazio
  // se o usuário só clicar Continuar (ver DEFAULT_SPECIALTY_CODE).
  useEffect(() => {
    if (!specialtiesQuery.data || specialtyIds.length > 0) return;
    const defaultSpecialty = specialtiesQuery.data.find(
      (s) => s.code === DEFAULT_SPECIALTY_CODE,
    );
    if (defaultSpecialty) setSpecialtyIds([defaultSpecialty.id]);
  }, [specialtiesQuery.data, specialtyIds.length]);

  const complete = trpc.me.completeProfile.useMutation({
    onSuccess: (r) => {
      // Só descarta o token quando o SERVIDOR confirma o aceite. Quem escolhe "Laboratório" no
      // passo 1 completa o perfil sem aceitar convite nenhum (o aceite exige organização de
      // clínica) — jogar o token fora ali deixava a pessoa sem nenhum caminho de volta pro
      // convite, que é por link e não tem outra porta. Mantido, o PendingConnectInviteBanner
      // abre a folha de escolha assim que ela entra no app.
      if (r?.conviteDeParceriaAceito) clearPendingConnectInvite();
      // Laboratório segue pro "Montar catálogo" (página própria, levada pelo AppLayout).
      onComplete();
    },
    onError: (err) =>
      setError(err.message || "Erro ao salvar perfil. Tente novamente."),
  });

  const totalDePassos = 2;

  // Seleção única no cadastro inicial — clicar numa opção substitui a anterior em vez de acumular.
  const toggleType = (t: OrganizationType) => setTypes([t]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (types.length === 0) {
      setStep(1);
      return;
    }

    // Quem chegou por link de convite de parceria traz o token no navegador — o servidor aceita
    // o convite na mesma transação que cria a organização pessoal, e a pessoa sai do onboarding
    // já conectada ao laboratório.
    const convitePendente = getPendingConnectInvite();
    complete.mutate({
      name: userName.trim(),
      phone: userPhone || undefined,
      cpf,
      croNumber: croNumber.trim(),
      croState: croState as never,
      types,
      specialtyIds,
      address: enderecoParaSalvar(address),
      connectInviteToken:
        convitePendente?.kind === "partner" ? convitePendente.token : undefined,
    });
  };

  return (
    <div className="w-full">
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm text-brand-700">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-600" />
          Passo {step} de {totalDePassos}
        </div>
        <h1 className="text-2xl font-bold text-zinc-900 sm:text-3xl">
          {step === 1 ? "Qual é a sua área de atuação?" : "Vamos te conhecer"}
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-zinc-500">
          {step === 1
            ? "A área e as especialidades podem ser mudadas depois em Configurações."
            : "Seus dados pessoais e registro profissional. Leva menos de um minuto."}
        </p>
      </div>

      {step === 1 ? (
        <OrgTypeStep
          selected={types}
          onToggle={toggleType}
          specialties={specialtiesQuery.data ?? []}
          selectedSpecialtyIds={specialtyIds}
          onSpecialtiesChange={setSpecialtyIds}
          onNext={() => setStep(2)}
          secondaryAction={secondaryAction}
        />
      ) : (
        <ProfileStep
          avatarSrc={user?.image}
          onAvatarChange={() => utils.me.current.invalidate()}
          userName={userName}
          userPhone={userPhone}
          cpf={cpf}
          croNumber={croNumber}
          croState={croState}
          address={address}
          rotuloDoEndereco={types.includes("DENTAL_LAB") ? "Endereço do laboratório" : "Endereço do consultório"}
          onAddressChange={(parcial) => setAddress((a) => ({ ...a, ...parcial }))}
          error={error}
          isPending={complete.isPending}
          onUserNameChange={setUserName}
          onUserPhoneChange={setUserPhone}
          onCpfChange={setCpf}
          onCroNumberChange={setCroNumber}
          onCroStateChange={setCroState}
          onSubmit={handleSubmit}
          onBack={() => setStep(1)}
        />
      )}
    </div>
  );
}
